/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useState } from "react";
import { toast } from "react-toastify";
import "react-phone-number-input/style.css";
import { useNavigate } from "react-router-dom";
import { proxyAPI } from "../proxyAPI";
import CommFieldsIcon from "../../assets/icons/comm_fields.svg2";
import LocationIcon from "../../assets/icons/icon_address-01.svg2";
import FullScreenLoadingIndicator from "../Common/FullScreenLoadingIndicator";
import ContactsIcon from "../../assets/icons/contact_edit.svg2";
import PageHeader from "../Common/PageHeader";
import { useGetContactFieldsQuery } from "../store/slices/contactsSlice";
import ContactField from "./ContactField";
import ContactLocationMap from "./ContactLocationMap";
import {
  useGetAssetsByContactQuery,
  useSetContactsInLocationMutation,
} from "../store/slices/mapsSlice";
import AssetList from "../Assets/AssetList";
import { isUserPermitted } from "../helpers/pageProps";
import { Col, Row } from "react-bootstrap";
import Logs from "../Dashboard/Logs";
import HelpButton from "../HelpContent/HelpButton";

export default function AddContact({ editedContact }) {
  const navigate = useNavigate();
  const { data: contactFields } = useGetContactFieldsQuery();

  const [loading, setLoading] = useState(false);
  const [contact, setContact] = useState(editedContact);
  const [errors, setErrors] = useState([]);
  const [markerOverlay, setMarkerOverlay] = useState();
  const [saveContactLocation] = useSetContactsInLocationMutation();
  const [address, setAddress] = useState("");
  const { data: assets } = useGetAssetsByContactQuery(contact.uuid, {
    skip: !contact.uuid,
  });

  const handleChange = ({ contactField, values }) => {
    setContact((prev) => {
      if (
        !prev.details.some((detail) => detail.field.uuid === contactField.uuid)
      ) {
        return {
          ...prev,
          details: [
            ...prev.details,
            {
              values: values.map((v) => ({ data: v })),
              field: {
                uuid: contactField.uuid,
              },
            },
          ],
        };
      }
      return {
        ...prev,
        details: prev.details.map((detail) => {
          if (detail.field.uuid === contactField.uuid) {
            return {
              ...detail,
              values: values.map((v) => ({ data: v })),
            };
          }
          return detail;
        }),
      };
    });
  };

  const handleSave = async () => {
    setLoading(true);

    const data = await proxyAPI({
      prefix: "contacts/v1",
      method: "POST",
      url: "/upsert",
      body: contact,
    });

    setTimeout(() => {
      setLoading(false);
    }, 500);

    setErrors(data?.errors || []);

    if (!data || data?.isError) {
      toast.error("There are errors");
      return;
    }

    toast.success("Saved contact successfully");

    if (data.contact.uuid) {
      if (markerOverlay) {
        const res = await saveContactLocation({
          Lat: markerOverlay.position.lat(),
          Lng: markerOverlay.position.lng(),
          items: [data.contact.uuid],
        });
        if (res.error) {
          toast.error("Failed to save location");
        }
        if (res.data) {
          markerOverlay.setMap(null);
          setMarkerOverlay(null);
        }
      }
      if (!editedContact.uuid) {
        navigate(`/assets/contacts/edit/${data.contact.uuid}`);
      } else {
        // Go back to contacts page after editing existing contact
        navigate("/assets/contacts/all");
      }
    }
  };

  const fieldsWithChannels = contactFields?.filter(
    (cf) => cf.isSms || cf.isEmail || cf.isVoice
  );

  const fieldsWithCards = contactFields?.filter((cf) => cf.isCard);

  return (
    contactFields && (
      <div className="h-100 d-flex flex-column mb-5">
        <FullScreenLoadingIndicator loading={loading} />
        <PageHeader
          Icon={ContactsIcon}
          heading={`${editedContact?.uuid ? "Edit" : "Add"} Contact`}
          subheading="Add a New Contact."
        >
          <div className="d-flex flex-wrap gap-2">
            {/* <WhiteButton
              as="RRLink"
              to="/assets/contacts/bulk"
              icon={ContactsIcon}
              heading="Manage"
              subheading="Bulk"
            />
            <WhiteButton
              as="button"
              icon={ContactsIcon}
              heading="Import"
              subheading="Contacts"
            />
            <WhiteButton
              as="RRLink"
              to="/assets/contacts/fields"
              icon={ContactsIcon}
              heading="Manage"
              subheading="Fields"
            /> */}
          </div>
        </PageHeader>
        <fieldset disabled={loading || !isUserPermitted("edit_contact")}>
          {!isUserPermitted("edit_contact") && (
            <div className="alert alert-warning">
              You do not have permission to edit this contact.
            </div>
          )}
          <div className="">
            <div className="bg-purple-gradient-darker-reverse rounded-4 py-3 px-5">
              <h5>
                <CommFieldsIcon width={30} className="me-2" />
                Communication Fields{" "}
                <HelpButton helpKey="contact-communication-fields" />
              </h5>
              <hr className="my-2 text-secondary" />
              <Row className="my-1">
                {fieldsWithChannels.map((contactField) => (
                  <Col
                    xs={12}
                    lg={6}
                    xxl={4}
                    key={contactField.uuid}
                    className="my-1"
                  >
                    <ContactField
                      key={contactField.uuid}
                      contactField={contactField}
                      contact={contact}
                      handleChange={handleChange}
                      errors={errors}
                    />
                  </Col>
                ))}
              </Row>
            </div>

            <div className=" p-0">
              <div className=" bg-purple-gradient-darker-reverse rounded-4 py-3 px-5 mt-4">
                <h5>
                  <CommFieldsIcon width={30} className="me-2" />
                  Card Fields <HelpButton helpKey="contact-card-fields" />
                </h5>
                <hr className="my-2 text-secondary" />
                <Row className="my-1">
                  {fieldsWithCards
                    .filter(
                      (cf) => !fieldsWithChannels.some((fwc) => fwc === cf)
                    )
                    .map((contactField) => (
                      <Col
                        xs={12}
                        lg={6}
                        xxl={4}
                        key={contactField.uuid}
                        className=" my-1"
                      >
                        <ContactField
                          key={contactField.uuid}
                          contactField={contactField}
                          contact={contact}
                          handleChange={handleChange}
                          errors={errors}
                        />
                      </Col>
                    ))}{" "}
                </Row>
              </div>

              <div className="py-3 bg-purple-gradient-darker-reverse rounded-4 px-5 mt-4">
                <h5>
                  <CommFieldsIcon width={30} className="me-2" />
                  Other Fields <HelpButton helpKey="contact-other-fields" />
                </h5>
                <hr className="my-2 text-secondary" />
                <Row>
                  {contactFields
                    .filter(
                      (cf) => !fieldsWithChannels.some((fwc) => fwc === cf)
                    )
                    .filter((cf) => !fieldsWithCards.some((fwc) => fwc === cf))
                    .map((contactField) => (
                      <Col
                        xs={12}
                        lg={6}
                        xxl={4}
                        key={contactField.uuid}
                        className="my-1"
                      >
                        <ContactField
                          key={contactField.uuid}
                          contactField={contactField}
                          contact={contact}
                          handleChange={handleChange}
                          errors={errors}
                        />
                      </Col>
                    ))}
                </Row>
              </div>
            </div>
          </div>
          <div className="bg-purple-gradient-darker-reverse rounded-4 px-5 py-3 mt-4">
            <h5 className="">
              <LocationIcon width={30} className="me-2" />
              Location <HelpButton helpKey="contact-location" />
            </h5>
            <hr className="my-3 text-secondary" />
            <div className="">
              <div className="">
                <div className="mt-3 rounded-3 overflow-hidden">
                  <ContactLocationMap
                    contactUuid={contact.uuid}
                    setMarkerOverlay={setMarkerOverlay}
                    setAddress={setAddress}
                  />
                  {markerOverlay && (
                    <div className="alert alert-danger mt-2">
                      You need to save the contact to save the location.
                    </div>
                  )}
                </div>
              </div>
              {/* <div className="col">
              <div className="my-3 bg-white p-3 rounded-3">
                <div className="row align-items-center">
                  <span className="col-4 small fw-semibold">Address</span>
                  <div className="col text-end">
                    <Form.Control
                      as="textarea"
                      aria-label="address"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div> */}
            </div>
          </div>
          {contact.uuid && (
            <>
              <h5 className="mt-5">
                <CommFieldsIcon width={30} className="me-2" />
                Assets
              </h5>
              <hr className="my-3 text-secondary" />
              <div className="row p-3 bg-white rounded-3">
                {assets ? (
                  <AssetList assets={assets} showTable />
                ) : (
                  "No assets found for this contact."
                )}
              </div>
            </>
          )}
          <div className="row justify-content-end my-3">
            <div className="col-auto">
              <button
                type="submit"
                className="btn btn-colored float-end fw-bold"
                onClick={handleSave}
              >
                Save contact
              </button>
            </div>
          </div>
        </fieldset>
      </div>
    )
  );
}
